<template>
  <div class="table-container">
    <div class="table">
      <div v-if="headers.length" class="table-header">
        <div class="table-cell spacing new-h3">
            &nbsp;
        </div>
        <div
          v-for="(header, index) in headers"
          :key="header"
          class="table-cell new-h3"
          :class="{
            'passthrough-header': showPassthroughHeader && index === 0,
            fade: highlightCell && !highlightCell.startsWith(header),
          }"
        >
          <slot
            :name="`header:${header}`"
            :header="header"
          >
            <span :class="{ 'passthrough-header-label': showPassthroughHeader && index === 0 }">
              {{ header }}
            </span>
            <span v-if="showPassthroughHeader && index === 0" class="passthrough-header-text">
              Pass-through
            </span>
          </slot>
        </div>
      </div>
      <div
        v-if="totals.length"
        class="table-row totals"
        :class="{ 'cursor-pointer': totalRowClickable }"
        @click="$emit('total-row-click')"
      >
        <slot name="total:label">
          <div class="table-cell spacing value">
            &nbsp;
          </div>
        </slot>
        <div
          v-for="total in totals"
          :key="total.id"
          class="table-cell passthrough"
          :class="{ value: !total.isPassthrough, fade: highlightCell }"
        >
          <slot name="total:cell" :total="total">
            <slot :name="`total:${total.id}`" :total="total">
              <PassthroughCell
                v-if="total.isPassthrough"
                :value="total.value"
                :percent="total.percent"
              />
              <div v-else class="new-body-text">
                {{ formatValue(total.value) }}
              </div>
            </slot>
          </slot>
        </div>
      </div>
      <div
        v-for="item in rows"
        :key="item.label"
        class="table-row"
        :class="{ [item.rowClass]: Boolean(item.rowClass) }"
      >
        <div v-if="!item?.children?.length" class="table-row">
          <div
            class="table-cell value label new-h3"
            :class="{
              fade: highlightCell && !highlightCell.endsWith(item.label),
              highlight: highlightCell && highlightCell.endsWith(item.label),
            }"
          >
            <slot :name="`row:label:${item.id}`" :item="item">
              {{ item.label }}
            </slot>
          </div>
          <slot v-for="key in Object.keys(item)" :key="key" :item="item" :name="`row:cell:${item.id}`">
            <InsightsTableCell
              v-if="!TABLE_KEYS_TO_EXCLUDE.includes(key)"
              :item="item"
              :item-key="key"
              :class="{
                fade: highlightCell && (!highlightCell.endsWith(item.label)
                  || !highlightCell.toLowerCase().includes(key)),
                highlight: highlightCell && (highlightCell.endsWith(item.label)
                  && highlightCell.toLowerCase().includes(key)),
              }"
            >
              <template #after-value>
                <slot :name="`after-value:${item.label}:${key}`" :item="item" />
              </template>
            </InsightsTableCell>
          </slot>
        </div>
        <div v-else class="table-row">
          <div class="table-row cursor-pointer" @click="openedRow = openedRow === item.label ? null : item.label">
            <div
              class="table-cell value label new-h3"
              :class="{
                fade: highlightCell && !highlightCell.endsWith(item.label),
                highlight: highlightCell && highlightCell.endsWith(item.label),
              }"
            >
              {{ item.label }}
              <v-icon :icon="openedRow === item.label ? '$chevron-up' : '$chevron-down'" />
            </div>
            <slot v-for="key in Object.keys(item)" :key="key" :item="item" :name="`row:cell:${item.id}`">
              <InsightsTableCell
                v-if="!TABLE_KEYS_TO_EXCLUDE.includes(key)"
                :item="item"
                :item-key="key"
                :class="{
                  fade: highlightCell && (!highlightCell.endsWith(item.label)
                    || !highlightCell.toLowerCase().includes(key)),
                  highlight: highlightCell && (highlightCell.endsWith(item.label)
                    && highlightCell.toLowerCase().includes(key)),
                }"
              >
                <template #after-value>
                  <slot :name="`after-value:${item.label}:${key}`" :item="item" />
                </template>
              </InsightsTableCell>
            </slot>
          </div>
          <div v-if="openedRow === item.label" class="table-row">
            <div v-for="child in item.children" :key="child.label" class="table-row">
              <div class="table-cell value label new-h3 pl-6">
                {{ child.label }}
              </div>
              <slot v-for="key in Object.keys(child)" :key="key" :item="child" :name="`row:cell:${child.id}`">
                <InsightsTableCell
                  v-if="!TABLE_KEYS_TO_EXCLUDE.includes(key)"
                  :item="child"
                  :item-key="key"
                />
              </slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import PassthroughCell from '@/components/insights/ProcessInsights/DemographicSurveyReport/tables/PassthroughCell.vue';
import { formatValue, TABLE_KEYS_TO_EXCLUDE } from '@/components/insights/helpers';
import InsightsTableCell from '@/components/insights/table/InsightsTableCell.vue';
import { ref } from 'vue';

defineProps({
  rows: {
    type: Array,
    default: () => [],
  },
  headers: {
    type: Array,
    default: () => [],
  },
  totals: {
    type: Array,
    default: () => [],
  },
  showPassthroughHeader: {
    type: Boolean,
    default: false,
  },
  totalRowClickable: {
    type: Boolean,
    default: false,
  },
  // Highlights a given cell. Format is columnLabel:rowLabel cell coordinates.
  highlightCell: {
    type: String,
    default: undefined,
  },
});

defineEmits(['total-row-click']);

const openedRow = ref(null);
</script>

<style lang="less" scoped>
.table-container {
  width: 100%;
}

.table {
  display: grid;
  grid-template-columns: minmax(130px, 200px) repeat(4, minmax(150px, 1fr)) minmax(80px, 150px);
}

.totals .table-cell:not(.spacing) {
  border-radius: 4px 4px 0px 0px;
  background: @bg-canvas;
}

.table-header {
  display: contents;
  font-weight: bold;

  .table-cell {
    color: @text-darkest;
  }
}

.table-row {
  display: contents;
}

.table-cell {
  max-height: 33px;

  :deep(.percentage) {
    font-size: 12px
  }

  &:not(:first-child):not(:last-child) {
    margin-right: 12px;
  }

  &:not(.passthrough) {
    padding: 8px;
  }

  &.value {
    padding: 8px;
    color: @text-darkest;
    border-bottom: 1px solid @stroke-default;

    &.label {
      width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.passthrough-header {
  display: flex;
  align-items: center;
  padding-right: 0px !important;
}

.passthrough-header-label {
  width: calc(100% - 70px);
}

.passthrough-header-text {
  font-size: 10px;
  font-weight: 500;
  line-height: 100%;
  color: @text-dark;
  vertical-align: bottom;
  height: 21px;
  display: flex;
  align-items: flex-end;
  min-width: 70px;
  width: 70px;
}

.fade {
  opacity: 0.3;
}

.highlight {
  border-bottom: 1px solid fade(@grey-500, 30%) !important;
}
</style>
